<script>
import { mapGetters } from 'vuex';
import { useReportMetrics } from 'dashboard/composables/useReportMetrics';
import { GROUP_BY_FILTER, METRIC_CHART } from './constants';
import fromUnixTime from 'date-fns/fromUnixTime';
import format from 'date-fns/format';
import {
  formatReportDuration,
  hasChartData,
} from 'dashboard/helper/reportDuration';
import ChartStats from './components/ChartElements/ChartStats.vue';
import FirstResponseOrigin from './components/ChartElements/FirstResponseOrigin.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import { toIntlLocale } from 'shared/helpers/intlLocale';

export default {
  components: { ChartStats, FirstResponseOrigin, BarChart },
  props: {
    groupBy: {
      type: Object,
      default: () => ({}),
    },
    accountSummaryKey: {
      type: String,
      default: 'getAccountSummary',
    },
    summaryFetchingKey: {
      type: String,
      default: 'getAccountSummaryFetchingStatus',
    },
    reportKeys: {
      type: Object,
      default: () => ({
        CONVERSATIONS: 'conversations_count',
        INCOMING_MESSAGES: 'incoming_messages_count',
        OUTGOING_MESSAGES: 'outgoing_messages_count',
        FIRST_RESPONSE_TIME: 'avg_first_response_time',
        RESOLUTION_TIME: 'avg_resolution_time',
        RESOLUTION_COUNT: 'resolutions_count',
        REPLY_TIME: 'reply_time',
      }),
    },
  },
  setup(props) {
    const { calculateTrend, isAverageMetricType } = useReportMetrics(
      props.accountSummaryKey
    );
    return { calculateTrend, isAverageMetricType };
  },
  computed: {
    ...mapGetters({
      accountReport: 'getAccountReports',
    }),
    metrics() {
      const reportKeys = Object.keys(this.reportKeys);
      const infoText = {
        FIRST_RESPONSE_TIME: this.$t(
          `REPORT.METRICS.FIRST_RESPONSE_TIME.INFO_TEXT`
        ),
        RESOLUTION_TIME: this.$t(`REPORT.METRICS.RESOLUTION_TIME.INFO_TEXT`),
        REPLY_TIME: this.$t(`REPORT.METRICS.REPLY_TIME.INFO_TEXT`),
      };
      return reportKeys.map(key => ({
        NAME: this.$t(`REPORT.METRICS.${key}.NAME`),
        KEY: this.reportKeys[key],
        DESC: this.$t(`REPORT.METRICS.${key}.DESC`),
        INFO_TEXT: infoText[key],
        TOOLTIP_TEXT: `REPORT.METRICS.${key}.TOOLTIP_TEXT`,
        trend: this.calculateTrend(this.reportKeys[key]),
      }));
    },
  },
  methods: {
    hasChartData,
    // O eixo do gráfico saía sempre em inglês ("13-Sep") porque o `format` do
    // date-fns usa o locale padrão da biblioteca, não o do app. Aqui o rótulo
    // passa a seguir o idioma da sessão.
    chartDateLabel(date, options) {
      const locale = toIntlLocale(this.$i18n?.locale || 'pt_BR');
      try {
        return new Intl.DateTimeFormat(locale, options).format(date);
      } catch (error) {
        return new Intl.DateTimeFormat('pt-BR', options).format(date);
      }
    },
    getCollection(metric) {
      if (!this.accountReport.data[metric.KEY]) {
        return {};
      }
      const data = this.accountReport.data[metric.KEY];
      const labels = data.map(element => {
        const dayMonth = { day: '2-digit', month: 'short' };
        if (this.groupBy?.period === GROUP_BY_FILTER[5].period) {
          return this.chartDateLabel(fromUnixTime(element.timestamp), {
            ...dayMonth,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          });
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[2].period) {
          let week_date = new Date(fromUnixTime(element.timestamp));
          const first_day = week_date.getDate() - week_date.getDay();
          const last_day = first_day + 6;
          const week_first_date = new Date(week_date.setDate(first_day));
          const week_last_date = new Date(week_date.setDate(last_day));
          return `${this.chartDateLabel(
            week_first_date,
            dayMonth
          )} - ${this.chartDateLabel(week_last_date, dayMonth)}`;
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[3].period) {
          return this.chartDateLabel(fromUnixTime(element.timestamp), {
            month: 'short',
            year: 'numeric',
          });
        }
        if (this.groupBy?.period === GROUP_BY_FILTER[4].period) {
          return format(fromUnixTime(element.timestamp), 'yyyy');
        }
        return this.chartDateLabel(fromUnixTime(element.timestamp), dayMonth);
      });
      const datasets = METRIC_CHART[metric.KEY].datasets.map(dataset => {
        switch (dataset.type) {
          case 'bar':
            return {
              ...dataset,
              yAxisID: 'y',
              label: metric.NAME,
              data: data.map(element => element.value),
            };
          case 'line':
            return {
              ...dataset,
              yAxisID: 'y',
              label: this.metrics[0].NAME,
              data: data.map(element => element.count),
            };
          default:
            return dataset;
        }
      });
      return {
        labels,
        datasets,
      };
    },
    getChartOptions(metric) {
      const options = {
        scales: METRIC_CHART[metric.KEY].scales,
      };

      // Only add tooltip configuration for time-based metrics
      if (this.isAverageMetricType(metric.KEY)) {
        options.plugins = {
          tooltip: {
            callbacks: {
              label: ({ raw, dataIndex }) => {
                return this.$t(metric.TOOLTIP_TEXT, {
                  metricValue: formatReportDuration(raw),
                  conversationCount:
                    this.accountReport.data[metric.KEY][dataIndex]?.count || 0,
                });
              },
            },
          },
        };
      }

      return options;
    },
  },
};
</script>

<template>
  <div
    class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 px-6 py-5 shadow outline-1 outline outline-n-container rounded-2xl bg-n-solid-2 mt-4"
  >
    <div
      v-for="metric in metrics"
      :key="metric.KEY"
      class="p-4 mb-3 rounded-md"
    >
      <ChartStats
        :metric="metric"
        :account-summary-key="accountSummaryKey"
        :summary-fetching-key="summaryFetchingKey"
      />
      <!-- SiteUP CRM (relatorios r2, item C): sob a media de primeira resposta,
           o mesmo numero separado entre pessoa e robo. Sem o recorte, uma media
           de 1 segundo feita por automacao era lida como desempenho da equipe. -->
      <FirstResponseOrigin
        v-if="metric.KEY === 'avg_first_response_time'"
        :account-summary-key="accountSummaryKey"
      />
      <!-- O vazio de ~400 px entre as duas linhas de numeros saia daqui: a
           caixa do grafico tinha h-72 e o filho tinha h-72 DE NOVO (288 px
           empilhados), e a condicao de desenhar era so "a serie chegou", nao
           "a serie tem algum valor". Serie toda zerada desenhava um grafico
           com todas as barras na altura zero: um retangulo em branco. Agora a
           altura e uma so e serie sem valor mostra o texto de sem dado. -->
      <div class="mt-4 h-72 flex items-center justify-center">
        <woot-loading-state
          v-if="accountReport.isFetching[metric.KEY]"
          class="text-xs"
          :message="$t('REPORT.LOADING_CHART')"
        />
        <BarChart
          v-else-if="hasChartData(accountReport.data[metric.KEY])"
          :collection="getCollection(metric)"
          :chart-options="getChartOptions(metric)"
        />
        <span v-else class="text-sm text-n-slate-10">
          {{ $t('REPORT.NO_ENOUGH_DATA') }}
        </span>
      </div>
    </div>
  </div>
</template>
